import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { Link } from '@tanstack/react-router';
import { CheckCircle } from 'lucide-react';
import { sanitizePhoneInput } from '@/lib/utils';

export function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedSubscription, setAcceptedSubscription] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const canSubmit = acceptedTerms && acceptedSubscription && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch', 'Passwords do not match'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('auth.passwordMinLength', 'Password must be at least 8 characters'));
      return;
    }

    setLoading(true);
    try {
      await register(
        form.email,
        form.password,
        form.fullName,
        form.phone,
        form.companyName || undefined,
        true,
      );
      setRegistered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.registrationFailed', 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-10 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.registrationSubmitted', 'Check Your Email!')}</h2>
          <p className="text-gray-600 mb-2">
            We sent a verification link to your email address. <strong>Please verify your email first</strong>, then wait for admin approval.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Check your inbox (and spam folder). Once verified and approved, you can log in.
          </p>
          <Link
            to="/login"
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2.5 rounded-lg transition"
          >
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.createOwnerAccount', 'Create Owner Account')}</h1>
          <p className="text-gray-500 mt-2">{t('auth.registerSubtitle', 'Register to start managing your hostel')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">

            {/* Personal details */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.fullName')} *</label>
                <input type="text" required value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} className={inp} placeholder="John Doe" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.companyName', 'Business / Company Name')}</label>
                <input type="text" value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} className={inp} placeholder="ABC Hostels Pvt Ltd (optional)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')} *</label>
                <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inp} placeholder="you@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.phone')} *</label>
                <input type="tel" inputMode="tel" required value={form.phone} onChange={e => setForm(f => ({ ...f, phone: sanitizePhoneInput(e.target.value) }))} className={inp} placeholder="+91 9999999999" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')} *</label>
                <input type="password" required value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className={inp} placeholder="8+ characters" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')} *</label>
                <input type="password" required value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} className={inp} placeholder="Repeat password" />
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('auth.requiredAgreements', 'Required Agreements')}</p>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={e => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">
                  {t('auth.agreeTermsPrefix', 'I have read and agree to the')}{' '}
                  <a href="#" className="text-teal-600 underline hover:text-teal-800">{t('auth.termsAndConditions', 'Terms & Conditions')}</a>{' '}
                  {t('common.and', 'and')}{' '}
                  <a href="#" className="text-teal-600 underline hover:text-teal-800">{t('auth.privacyPolicy', 'Privacy Policy')}</a>.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedSubscription}
                  onChange={e => setAcceptedSubscription(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">
                  {t('auth.subscriptionAgreement', 'I understand and agree to pay the applicable subscription fee for using this Hostel Management System. I acknowledge that my account and services are subject to the agreed payment terms.')}
                </span>
              </label>
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? t('auth.submitting', 'Submitting…') : t('auth.createAccount', 'Create Account')}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-teal-600 font-medium hover:underline">{t('auth.signIn')}</Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          {t('auth.allAccountsApproval', 'All accounts require admin approval before first login.')}
        </p>
      </div>
    </div>
  );
}
